// ============================================================
//  src/oauth-drive.js — Flujo OAuth 2.0 para Google Drive
//  Reemplaza signInWithPopup — abre ventana de consentimiento
//  y recibe el authorization code vía postMessage desde
//  oauth-callback.html (redirect_uri).
// ============================================================

const OAUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const CLIENT_ID      = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const REDIRECT_URI   = import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI
    ?? `${location.origin}/oauth-callback.html`;
const DRIVE_SCOPE    = 'https://www.googleapis.com/auth/drive.file';

// Clave donde se guarda el código pendiente de intercambio en backend
export const DRIVE_PENDING_CODE_KEY = 'drive_oauth_pending_code';

// ── URL de autorización ───────────────────────────────────────

function _generarUrlOAuth() {
    const params = new URLSearchParams({
        client_id:     CLIENT_ID,
        redirect_uri:  REDIRECT_URI,
        response_type: 'code',
        scope:         DRIVE_SCOPE,
        access_type:   'offline',
        prompt:        'consent',
    });
    return `${OAUTH_ENDPOINT}?${params}`;
}

// ── Helpers defensivos contra COOP ────────────────────────────

function _popupClosed(popup) {
    try { return popup.closed; } catch { return true; }
}

function _popupClose(popup) {
    try { if (!popup.closed) popup.close(); } catch { /* COOP */ }
}

// ── Apertura de ventana y escucha del código ──────────────────

/**
 * Abre la pantalla de consentimiento de Google en una ventana popup.
 *
 * Flujo:
 *   1. Abre accounts.google.com con los parámetros OAuth
 *   2. El usuario autoriza → Google redirige a REDIRECT_URI con ?code=...
 *   3. oauth-callback.html envía el código de vuelta via postMessage
 *   4. La Promise resuelve con el authorization code (string)
 *
 * @returns {Promise<string>}  authorization code de Google
 */
export function abrirVentanaOAuthDrive() {
    return new Promise((resolve, reject) => {
        if (!CLIENT_ID) {
            reject(new Error('popup-blocked'));
            console.error('[OAuthDrive] VITE_GOOGLE_CLIENT_ID no está configurado en .env');
            return;
        }

        const url   = _generarUrlOAuth();
        const popup = window.open(
            url,
            'drive-oauth',
            'width=600,height=700,left=200,top=100,resizable=yes,scrollbars=yes'
        );

        if (!popup || _popupClosed(popup)) {
            reject(new Error('popup-blocked'));
            return;
        }

        function onMessage(event) {
            if (event.origin !== location.origin) return;
            const d = event.data;
            if (!d || d.type !== 'oauth-drive-callback') return;

            cleanup();

            if (d.code) {
                resolve(d.code);
            } else {
                reject(new Error(d.error ?? 'oauth-cancelled'));
            }
        }

        // Detecta si el usuario cierra la ventana manualmente
        const pollCierre = setInterval(() => {
            if (_popupClosed(popup)) {
                cleanup();
                reject(new Error('popup-closed'));
            }
        }, 1000);

        function cleanup() {
            clearInterval(pollCierre);
            window.removeEventListener('message', onMessage);
            _popupClose(popup);
        }

        window.addEventListener('message', onMessage);
    });
}
